import React, { forwardRef, useState, useCallback, useId } from 'react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export type SwitchSize = 'sm' | 'md' | 'lg';

export interface SwitchProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  size?: SwitchSize;
}

const trackSizes: Record<SwitchSize, string> = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
  lg: 'h-7 w-[3.25rem]',
};

const thumbSizes: Record<SwitchSize, { off: string; on: string; size: string }> = {
  sm: { off: 'translate-x-0.5', on: 'translate-x-[1.125rem]', size: 'h-4 w-4' },
  md: { off: 'translate-x-0.5', on: 'translate-x-[1.375rem]', size: 'h-5 w-5' },
  lg: { off: 'translate-x-0.5', on: 'translate-x-[1.625rem]', size: 'h-6 w-6' },
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      className,
      checked: controlledChecked,
      defaultChecked = false,
      onChange,
      label,
      size = 'md',
      disabled,
      id: externalId,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const id = externalId ?? autoId;
    const [internal, setInternal] = useState(defaultChecked);

    const isChecked = controlledChecked ?? internal;

    const toggle = useCallback(() => {
      if (disabled) return;
      const next = !isChecked;
      if (controlledChecked === undefined) setInternal(next);
      onChange?.(next);
    }, [controlledChecked, disabled, isChecked, onChange]);

    const thumb = thumbSizes[size];

    return (
      <div className={cn('inline-flex items-center gap-2', className)}>
        <button
          ref={ref}
          id={id}
          type="button"
          role="switch"
          aria-checked={isChecked}
          aria-label={!label ? 'Toggle' : undefined}
          disabled={disabled}
          onClick={toggle}
          className={cn(
            'relative inline-flex shrink-0 cursor-pointer items-center rounded-full transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            'dark:focus-visible:ring-offset-gray-900',
            'disabled:cursor-not-allowed disabled:opacity-50',
            isChecked
              ? 'bg-primary-900 dark:bg-primary-200'
              : 'bg-gray-300 dark:bg-gray-600',
            trackSizes[size]
          )}
          {...props}
        >
          <span
            aria-hidden="true"
            className={cn(
              'rounded-full bg-white shadow transition-transform duration-200',
              thumb.size,
              isChecked ? thumb.on : thumb.off
            )}
          />
        </button>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
